import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useAgent } from './hooks/useAgent';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { AgentFeature, AgentMessage, AgentQueryResponse, AgentRole } from './types';
import { useTranslation } from 'react-i18next';

const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f8f9fa;
  border-radius: 12px 12px 0 0;
`;

const ChatTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background-color: #ffffff;
`;

interface AgentChatProps {
  siteId?: string;
  feature: AgentFeature;
  filters?: {
    status?: string;
    name?: string;
    session_timeout?: string;
  };
  onClose: () => void;
  onResponse?: (newResponse: AgentQueryResponse['data']) => void;
}

// TODO it's becoming a campaign agent chat, so we should rename it and abstract common logic to a new component as soon as we start to implement differently scoped agents
export function AgentChat({ siteId, filters, feature, onClose, onResponse }: AgentChatProps) {
  const { t } = useTranslation('agents');
  const thinkingMessagePath = 'campaignAssistant.thinkingMessage';
  const [messages, setMessages] = useState<AgentMessage[]>([
    {
      role: AgentRole.Assistant,
      content: '',
      i18nKey: 'campaignAssistant.welcomeMessage',
      timestamp: new Date(),
    },
  ]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { queryAgent, isLoading } = useAgent();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = (userMessage: string) => {
    const convoHistory = [...messages];
    const now = new Date();
    // Update messages with user message
    const userMsg: AgentMessage = {
      role: AgentRole.User,
      content: userMessage,
      timestamp: now,
    };
    setMessages(prev => [...prev, userMsg]);

    // Add loading message
    const loadingMsg: AgentMessage = {
      role: AgentRole.Assistant,
      content: '',
      i18nKey: thinkingMessagePath,
      timestamp: now,
    };
    setMessages(prev => [...prev, loadingMsg]);

    // Query agent with page context
    // TODO: add timestamp?
    queryAgent(
      {
        query: userMessage,
        siteId,
        feature: feature,
        conversationHistory: convoHistory.map(msg => ({
          role: msg.role,
          content: msg.content,
        })),
        context: {
          siteId,
          filters,
        },
      },
      {
        onSuccess: response => {
          // Remove loading message and add agent response
          setMessages(prev => {
            const withoutLoading = prev.filter(msg => msg.i18nKey !== thinkingMessagePath);
            return [
              ...withoutLoading,
              {
                role: AgentRole.Assistant,
                content: response.data?.response || t('campaignAssistant.errorMessage'),
                timestamp: new Date(),
              },
            ];
          });
          onResponse?.(response.data?.data);
        },
        onError: () => {
          // Remove loading message and add error message
          setMessages(prev => {
            const withoutLoading = prev.filter(msg => msg.i18nKey !== thinkingMessagePath);
            return [
              ...withoutLoading,
              {
                role: AgentRole.Assistant,
                content: t('campaignAssistant.errorMessage'),
                timestamp: new Date(),
              },
            ];
          });
        },
      },
    );
  };

  return (
    <>
      <ChatHeader>
        <ChatTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src="/images/campy_with_hat_small.png" alt={t('campaignAssistant.agentName')} width={40} height={40} />
            <span> {t('campaignAssistant.chatTitle', { chatTitle: t('campaignAssistant.agentName') })}</span>
          </div>
        </ChatTitle>
        <CloseButton onClick={onClose} title="Close">
          ×
        </CloseButton>
      </ChatHeader>
      <MessagesContainer>
        <>
          {messages.map((message, index) => (
            <ChatMessage key={index} message={message} />
          ))}
          <div ref={messagesEndRef} />
        </>
      </MessagesContainer>
      <ChatInput onSend={handleSend} disabled={isLoading} />
    </>
  );
}
